import { getSwat, fetchLists, deleteFromLists, onWishlistChange, notifyWishlistChange } from '@theme/wishlist';
import { adoptSectionStylesheet } from '@theme/section-renderer';
import { getPreferencesConsent, onPreferencesConsentChange, grantPreferencesConsent } from '@theme/cookie-consent';

/** How long to wait for the Swym SDK before assuming it won't load (app embed off, remote kill-switch). */
const SDK_TIMEOUT_MS = 10000;

/**
 * The outer bound on holding `loading` for an unanswered banner. Much longer
 * than the SDK wait, because here we are waiting on a person reading a dialog
 * rather than on a script, and a visitor who is still deciding must not be told
 * the wishlist failed.
 *
 * It exists because `pending` is not always transient. Cookiebot licenses its
 * banner per domain, so on a host outside the domain group (a
 * `*.shopifypreview.com` share link, for one) `window.Cookiebot` loads, refuses
 * to render the dialog, and leaves `hasResponse` false for good. Without a cap
 * the page waits on an answer nobody can give and spins forever. Answering at
 * any point re-runs #reflectConsent and puts the loading state back, so an
 * expiry under a banner that is merely being read slowly is recoverable rather
 * than final.
 */
const PENDING_TIMEOUT_MS = 30000;

/**
 * The wishlist page grid rendered by sections/wishlist.liquid. Reads the
 * visitor's Swym lists, then renders each entry as a real theme product card
 * by fetching this same section on the product's URL through the Section
 * Rendering API (?section_id=...&variant=<epi>), so the cards stay pixel
 * identical to the PLP/cart cross-sell ones.
 */
class WishlistPageGrid extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {Function | null} */
  #unsubscribe = null;
  /** @type {Function | null} */
  #unsubscribeConsent = null;
  /** @type {Object | null} */
  #swat = null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timeout;
  #renderToken = 0;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) return;

        const remove = event.target.closest('[data-wishlist-remove]');
        if (remove) {
          this.#remove(remove);
          return;
        }

        // Unlike the toggle's dialog, there is nothing to close first: this is
        // a region on the page, not a modal. #reflectConsent() takes the panel
        // back to loading on the grant event.
        if (event.target.closest('[data-wishlist-consent-grant]')) grantPreferencesConsent();
      },
      { signal }
    );

    this.#unsubscribe = onWishlistChange(() => this.#render());
    this.#unsubscribeConsent = onPreferencesConsentChange(() => this.#reflectConsent());
    this.#init();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#unsubscribe?.();
    this.#unsubscribeConsent?.();
    clearTimeout(this.#timeout);
  }

  async #init() {
    this.#reflectConsent();

    this.#swat = await getSwat();
    clearTimeout(this.#timeout);
    if (this.isConnected) this.#render();
  }

  /**
   * Applies the current consent state, on mount and on every Cookiebot event.
   *
   * Blocked (a declined Preferences cookie category, VIS-956) renders straight
   * away rather than after the SDK timeout, because it is known from Cookiebot
   * alone and it is the one cause the visitor can clear. Pending holds the
   * loading state on a much longer clock: the banner is on screen, so timing
   * out at the SDK's ten seconds would blame us for a dialog the visitor simply
   * hasn't answered yet. Answering restarts the wait, so accepting and then
   * getting no SDK still ends on the error rather than on loading forever.
   */
  #reflectConsent() {
    if (this.#swat) return;

    const consent = getPreferencesConsent();
    if (consent === 'blocked') {
      this.#show('consent');
      return;
    }

    // Back to waiting, the same way the toggle goes back to hidden: leaving the
    // panel up would keep telling someone who has just accepted that they
    // declined. A no-op before anything was shown, since loading is the state
    // the page ships in.
    this.#show('loading');

    this.#armTimeout(consent === 'pending' ? PENDING_TIMEOUT_MS : SDK_TIMEOUT_MS);
  }

  /**
   * Starts, or restarts, the wait. Once it runs out the only explanations left
   * are ones the visitor cannot clear (app embed off, Swym's remote
   * kill-switch, a CMP that never manages to ask), which is what the generic
   * error is for.
   *
   * @param {number} delay
   */
  #armTimeout(delay) {
    clearTimeout(this.#timeout);
    this.#timeout = setTimeout(() => {
      // A decline landing mid-wait owns the state instead.
      if (this.#swat || getPreferencesConsent() === 'blocked') return;
      this.#show('error');
    }, delay);
  }

  async #render() {
    if (!this.#swat) return;
    const token = ++this.#renderToken;

    let items;
    try {
      const lists = await fetchLists(this.#swat);
      items = lists.flatMap((list) => list.listcontents || []);
    } catch {
      if (token === this.#renderToken) this.#show('error');
      return;
    }
    if (token !== this.#renderToken) return;

    if (items.length === 0) {
      this.#show('empty');
      return;
    }

    // Newest first; Swym stamps entries with cts.
    items.sort((a, b) => (b.cts || 0) - (a.cts || 0));

    // One card per variant: the same product can sit in more than one list, and
    // every list is read above. Deduped after the sort, so the entry that
    // survives is the most recently saved one.
    const seen = new Set();
    items = items.filter((item) => {
      const key = `${item.empi}:${item.epi}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    const cards = await Promise.all(items.map((item) => this.#fetchCard(item)));
    if (token !== this.#renderToken) return;

    const rendered = cards.filter(Boolean);
    if (rendered.length === 0) {
      this.#show('empty');
      return;
    }

    const grid = this.querySelector('[data-wishlist-grid]');
    grid?.replaceChildren(...rendered);
    this.#show('grid');
  }

  /**
   * Renders one wishlist entry into a grid item via the Section Rendering API.
   * @param {Object} item - A Swym listcontents entry ({ empi, epi, du, ... }).
   * @returns {Promise<Element | null>}
   */
  async #fetchCard(item) {
    if (!item.du) return null;

    let url;
    try {
      url = new URL(item.du, window.location.origin);
    } catch {
      return null;
    }

    const params = new URLSearchParams({ section_id: this.dataset.sectionId ?? '' });
    if (item.epi) params.set('variant', String(item.epi));

    try {
      const response = await fetch(`${url.pathname}?${params}`);
      // Deleted / unpublished products must not break the rest of the grid.
      if (!response.ok) return null;

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      // This page renders no product card of its own, so its CSS only arrives with the fragments.
      adoptSectionStylesheet(html);
      return html.querySelector('[data-wishlist-item]');
    } catch {
      return null;
    }
  }

  /** @param {Element} button */
  async #remove(button) {
    if (!this.#swat || button.hasAttribute('disabled')) return;
    button.setAttribute('disabled', '');

    const payload = {
      empi: Number(button.getAttribute('data-product-id')),
      epi: Number(button.getAttribute('data-variant-id')),
      du: button.getAttribute('data-product-url'),
    };

    try {
      await deleteFromLists(this.#swat, payload);
      button.closest('[data-wishlist-item]')?.remove();
      if (!this.querySelector('[data-wishlist-item]')) this.#show('empty');
      notifyWishlistChange();
    } catch (error) {
      console.warn('Wishlist removal failed:', error);
      button.removeAttribute('disabled');
    }
  }

  /** @param {'loading' | 'grid' | 'empty' | 'error' | 'consent'} state */
  #show(state) {
    this.querySelector('[data-wishlist-loading]')?.toggleAttribute('hidden', state !== 'loading');
    this.querySelector('[data-wishlist-grid]')?.toggleAttribute('hidden', state !== 'grid');
    this.querySelector('[data-wishlist-empty]')?.toggleAttribute('hidden', state !== 'empty');
    this.querySelector('[data-wishlist-error]')?.toggleAttribute('hidden', state !== 'error');
    this.querySelector('[data-wishlist-consent]')?.toggleAttribute('hidden', state !== 'consent');
  }
}

if (!customElements.get('wishlist-page-grid')) {
  customElements.define('wishlist-page-grid', WishlistPageGrid);
}
